import axios from 'axios';
import { nanoid } from 'nanoid';

export type PutToS3Options = {
  /**
   * When false, omit Content-Type so the PUT matches a presign that did not
   * sign that header. Default true — other upload flows sign Content-Type.
   */
  includeContentType?: boolean;
};

export const putToS3 = async (
  file: File,
  presignedUrl: string,
  options?: PutToS3Options
) => {
  const includeContentType = options?.includeContentType !== false;

  if (!includeContentType) {
    // Axios PUT still emits Content-Type: application/x-www-form-urlencoded
    // when the header is simply omitted, and XHR/fetch will also send
    // file.type. Either one breaks an unsigned S3 presign
    // (SignatureDoesNotMatch). fetch + a typeless slice sends no
    // Content-Type at all.
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file.slice(0, file.size, ''),
    });
    return { status: response.status };
  }

  return axios.put(presignedUrl, file, {
    headers: file.type ? { 'Content-Type': file.type } : {},
  });
};

export const generateFileKey = (fileName: string) => {
  const lastDotPosition = fileName.lastIndexOf('.');

  if (lastDotPosition > 0 && lastDotPosition < fileName.length - 1) {
    fileName = fileName.substring(0, lastDotPosition);
  }

  // Sanitize the baseFilename for use in a path or key:
  let sanitizedBaseFilename = fileName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_-]/g, '')
    .substring(0, 30);

  if (!sanitizedBaseFilename) {
    sanitizedBaseFilename = 'file';
  }

  const randomId = nanoid(8);

  return `${sanitizedBaseFilename}-${randomId}`;
};
